import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ActionToastService, ActionToastVariant, ActionToastVm } from './action-toast.service';

/** The toast as the template draws it. Precomputed: no logic in the template (STANDARDS §1.5). */
export interface ActionToastView {
    readonly id: number;
    readonly variant: ActionToastVariant;
    /** The status pill sits 28px from the bottom, the lead toast 24px (C.9): a class on the centring wrapper. */
    readonly isStatus: boolean;
    readonly cardClass: 'vf-atoast__card--lead' | 'vf-atoast__card--status';
    /** The lead toast's green check; the 'plain' variant (P4: a failed Undo) has the same card without it. */
    readonly showCheck: boolean;
    readonly title: string;
    readonly sub: string;
    readonly hasSub: boolean;
    readonly action: ActionToastActionView | null;
}

export interface ActionToastActionView {
    readonly label: string;
    readonly showUndoIcon: boolean;
    /** The action is in flight: the button is disabled (and the toast held by the service). */
    readonly busy: boolean;
}

/** `current$`'s value as the template's view; null for no toast. */
export function toActionToastView(vm: ActionToastVm | null | undefined): ActionToastView | null {
    if (!vm) {
        return null;
    }
    const isStatus = vm.variant === 'status';
    return {
        id: vm.id,
        variant: vm.variant,
        isStatus,
        cardClass: isStatus ? 'vf-atoast__card--status' : 'vf-atoast__card--lead',
        showCheck: vm.variant === 'lead',
        title: vm.title,
        sub: vm.sub || '',
        hasSub: !!vm.sub,
        action: vm.action ? { label: vm.action.label, showUndoIcon: vm.action.icon === 'undo', busy: vm.action.busy } : null,
    };
}

/**
 * `vf-action-toast` (FE-53; MYDAY_LEAD_COMMS_PLAN.md Appendix C.9): THE one global action toast — `Moved to {Stage}`
 * (P3), `Lead follow-up completed · Undo` and the `Task completed` status pill (P4), LP's stage toast (P6). It draws
 * `ActionToastService.current$`; the service owns the one-at-a-time rule and the timer.
 *
 * - Hosted ONCE by the always-mounted MainLayout beside `vf-task-date-nudge` (FE-63, STANDARDS §1.13), so it shows on
 *   every main route, including after the panel that raised it has closed.
 * - Two looks (C.9): 'lead' — the design's dark lw toast with the green check, no entry animation (D:5916, D:8826);
 *   'status' — the guard-status pill with the clock, `vfPop` on the inner card (D:4813). Centring sits on the outer
 *   wrapper and the animation on the inner card, so the pill never drops its centring while it pops (the prototype puts
 *   both on one element, D:4813 — not copied). P4: 'plain' is the lead card without the check (a failed Undo).
 * - `role="status" aria-live="polite"` on the wrapper, which is always in the DOM, so a screen reader hears every new
 *   toast once (B.8.7). z-index 1340: above Bootstrap modals and the panel layers, below the 1350 tooltip layer.
 * - Hover / focus hold the timer and release it with the time that was left (S-13): every call names the toast's
 *   `id`, so a late leave never touches a newer toast. `show()` drops the holds of the toast it replaces, so when a new
 *   toast arrives while the pointer or the focus is still inside, the hold is asserted again for it — after the render,
 *   from what is actually hovered or focused, so a focus lost with a removed button never holds a toast for ever.
 */
@Component({
    selector: 'vf-action-toast',
    templateUrl: './action-toast.component.html',
    styleUrls: ['./action-toast.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActionToastComponent implements OnInit, AfterViewChecked, OnDestroy {
    /** What the template draws; null = no toast (the live region stays, empty). */
    view: ActionToastView | null = null;
    /** The wrapper's status-pill offset (bottom 28px instead of 24px). */
    isStatus = false;

    @ViewChild('card') private card?: ElementRef<HTMLElement>;

    private readonly destroy$ = new Subject<void>();
    private hovered = false;
    private focused = false;
    /** A replacement toast arrived while hovered or focused: re-assert the hold after it renders. */
    private reassertHold = false;

    constructor(
        private readonly toasts: ActionToastService,
        private readonly cdr: ChangeDetectorRef,
        private readonly zone: NgZone
    ) {}

    ngOnInit(): void {
        // Back in Angular's zone whatever raised the toast (a SignalR path runs outside it), so it always renders.
        this.toasts.current$.pipe(takeUntil(this.destroy$)).subscribe((vm) => this.zone.run(() => this.render(vm)));
    }

    ngAfterViewChecked(): void {
        if (!this.reassertHold) {
            return;
        }
        this.reassertHold = false;
        const card = this.card ? this.card.nativeElement : null;
        const view = this.view;
        if (!card || !view) {
            this.hovered = false;
            this.focused = false;
            return;
        }
        this.hovered = this.hovered && card.matches(':hover');
        this.focused = this.focused && containsFocus(card);
        if (this.hovered) {
            this.toasts.pause(view.id, 'hover');
        }
        if (this.focused) {
            this.toasts.pause(view.id, 'focus');
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onPointerEnter(): void {
        this.hovered = true;
        if (this.view) {
            this.toasts.pause(this.view.id, 'hover');
        }
    }

    onPointerLeave(): void {
        this.hovered = false;
        if (this.view) {
            this.toasts.resume(this.view.id, 'hover');
        }
    }

    onFocusIn(): void {
        this.focused = true;
        if (this.view) {
            this.toasts.pause(this.view.id, 'focus');
        }
    }

    /** Focus left the toast (not just moved between its own controls). */
    onFocusOut(event: FocusEvent): void {
        const card = this.card ? this.card.nativeElement : null;
        const next = event ? (event.relatedTarget as Node | null) : null;
        if (card && next && card.contains(next)) {
            return;
        }
        this.focused = false;
        if (this.view) {
            this.toasts.resume(this.view.id, 'focus');
        }
    }

    onAction(event?: Event): void {
        if (this.view && this.view.action && !this.view.action.busy) {
            const button = event && event.currentTarget instanceof Element ? event.currentTarget.getBoundingClientRect() : null;
            this.toasts.runAction(this.view.id, button ? { top: button.top, bottom: button.bottom, left: button.left, right: button.right } : null);
        }
    }

    private render(vm: ActionToastVm | null): void {
        const previousId = this.view ? this.view.id : null;
        this.view = toActionToastView(vm);
        this.isStatus = !!this.view && this.view.isStatus;
        if (!this.view) {
            // The card left the DOM: no leave event will come for it.
            this.hovered = false;
            this.focused = false;
            this.reassertHold = false;
        } else if (previousId !== null && previousId !== this.view.id && (this.hovered || this.focused)) {
            this.reassertHold = true;
        }
        this.cdr.markForCheck();
    }
}

/** Focus is on `el` or inside it. */
function containsFocus(el: HTMLElement): boolean {
    const doc = el.ownerDocument;
    const active = doc ? doc.activeElement : null;
    return !!active && el.contains(active);
}
